import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { Send } from 'domains/campaigns/types';
import { Value, getOptionValues } from 'domains/core/components/Select/Select';
import FilterGroup from 'domains/segments/pages/NestedSegmentCreatePage/FilterGroup';
import {
    addNodeAtPath,
    countFilters,
    createFilterLeafNode,
    createGroupNode,
    filterTree,
    flattenFilters,
    pruneEmptyGroups,
    removeNodeAtPath,
    updateFilterAtPath,
    updateMatchTypeAtPath,
} from 'domains/segments/pages/NestedSegmentCreatePage/treeUtils';
import { FilterGroupNode } from 'domains/segments/pages/NestedSegmentCreatePage/types';
import {
    buildTreeFromDefinition,
    findFirstEngagementName,
    getNestedSegmentDefinition,
    isNestedSegmentFiltersValid,
} from 'domains/segments/pages/NestedSegmentCreatePage/utils';
import AutocompleteInput from 'domains/segments/pages/SegmentCreatePage/Filter/FilterValueInput/AutocompleteInput';
import {
    NestedSegmentDefinitionInput,
    SegmentDefinition,
    SegmentFilterCategorySpecificField,
    SegmentFilterState,
} from 'domains/segments/types';
import { getDemographicFilters, getEngagementNameToValuesRef } from 'domains/segments/utils';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSends from 'hooks/queries/useSends';
import { FilterCategory, RecordType } from 'models/enums';

type StringOption = Omit<Value, 'label'> & {
    label: string;
};

const allContactsRecordType = {
    label: RecordType.ALL_CONTACTS,
    value: RecordType.ALL_CONTACTS,
};

const autocompleteInputSX = {
    sxAutocomplete: { width: '24rem' },
    sxChip: { textTransform: 'capitalize' },
    sxOption: { textTransform: 'capitalize' },
};

export const MAX_FILTER_LENGTH = 25;

const createEmptyRoot = (): FilterGroupNode => ({
    id: 'root',
    matchType: 'AND',
    children: [],
});

const getHasAllContacts = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.some(({ value }) => value === RecordType.ALL_CONTACTS);
const removeOtherOptions = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.filter(({ value }) => value === RecordType.ALL_CONTACTS);
const removeAllContactsOption = (recordTypeOptions: StringOption[]) =>
    recordTypeOptions.filter(({ value }) => value !== RecordType.ALL_CONTACTS);

export type Props = {
    /**
     * When provided, the builder pre-fills its tree + record types from this definition (edit/copy flow).
     * The definition is read once on mount (or when sends become available for engagement filters).
     */
    initialDefinition?: SegmentDefinition | null;
    /**
     * Emitted whenever the tree or record types change. `definition` is in the same shape the
     * backend/worker consume (identical to a saved segment's `segmentDefinition`). `isValid` is true
     * when every leaf filter is complete and at least one record type is selected.
     */
    onChange: (definition: NestedSegmentDefinitionInput, isValid: boolean) => void;
    /**
     * Extra sends to offer in the engagement-filter send pickers, merged ahead of the API-fetched
     * sends (deduped by id). Used by the campaign exit-criteria editor to surface the sends of the
     * campaign currently being built — including in-flight ones — exactly like conditional splits do.
     */
    additionalSends?: Send[];
};

/**
 * Reusable nested filter builder core. Owns the filter tree + record-type selection state, fetches
 * filter metadata, converts the tree to a SegmentDefinition, validates it, and emits both on every
 * change. Consumed by NestedSegmentCreatePage (saving a named audience) and the campaign exit-criteria
 * editor (snapshotting an inline definition onto the campaign).
 *
 * Page-level concerns (name input, save/preview buttons, navigation) stay with the consumer.
 */
const NestedFilterBuilder = ({ initialDefinition, onChange, additionalSends }: Props) => {
    const { contactObject: hasContactObjectFlag } = useFlags();

    const [rootGroup, setRootGroup] = useState<FilterGroupNode>(createEmptyRoot);
    const [recordTypeOptions, setRecordTypeOptions] = useState<StringOption[]>([]);
    const [recordTypeInputValue, setRecordTypeInputValue] = useState('');
    const [datetimeErrors, setDatetimeErrors] = useState<{ [key: string]: boolean }>({});
    const [valuesRef, setValuesRef] = useState('');

    // Counter for generating numeric filter IDs (SegmentFilterState.id)
    const nextFilterIdRef = useRef(0);
    const hasFetchedSendsRef = useRef(false);
    const prefillRunRef = useRef(false);

    const getNextFilterId = () => {
        const id = nextFilterIdRef.current;
        nextFilterIdRef.current += 1;
        return id;
    };

    const { data: segmentFilters, isSuccess: isSegmentFiltersSuccess } = useSegmentFilters({ version: 3 });

    const isRecordTypesEmpty = recordTypeOptions.length === 0;
    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;

    const segmentRecordTypeOptions: Value[] = useMemo(
        () => [
            allContactsRecordType,
            ...(segmentRecordTypes?.map(({ recordType }) => ({ label: recordType, value: recordType })) ?? []),
        ],
        [segmentRecordTypes]
    );

    const recordTypes = useMemo(() => getOptionValues(recordTypeOptions), [recordTypeOptions]);

    const hasAllContacts = useMemo(() => isRecordTypesEmpty || getHasAllContacts(recordTypeOptions), [
        isRecordTypesEmpty,
        recordTypeOptions,
    ]);

    const demographicFilters = useMemo(() => {
        if (!segmentFilters) return [];
        return getDemographicFilters({ hasAllContacts, recordTypes, segmentRecordTypes });
    }, [segmentFilters, hasAllContacts, recordTypes, segmentRecordTypes]);

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) return [];
        // recordTypes[0] holds engagement filters (single item, recordType "" from the API)
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const eventRecordTypeFilters = useMemo(() => {
        if (!segmentFilters) return [];
        return segmentFilters.contactEvent.recordTypes;
    }, [segmentFilters]);

    const objectRecordTypeFilters = useMemo(() => {
        if (!segmentFilters || !hasContactObjectFlag) return [];
        return segmentFilters.contactObject?.recordTypes ?? [];
    }, [segmentFilters, hasContactObjectFlag]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    const { data: sends } = useSends({
        path: valuesRef,
        reactQueryOptions: { enabled: Boolean(valuesRef) },
    });

    // Campaign-local sends (e.g. the in-flight campaign's own sends) take precedence over the
    // API-fetched list so they appear in the engagement send pickers even before they are persisted.
    // When no additional sends are supplied this is identical to `sends`, preserving existing behaviour.
    const mergedSends = useMemo(() => {
        if (!additionalSends || additionalSends.length === 0) return sends;
        const fetched = sends ?? [];
        const fetchedIds = new Set(fetched.map((send) => send.id));
        return [...additionalSends.filter((send) => !fetchedIds.has(send.id)), ...fetched];
    }, [sends, additionalSends]);

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors((prev) => ({ ...prev, [filterId]: error }));

    // ─── Tree mutation handlers ─────────────────────────────────────────

    const handleUpdateFilter = useCallback(
        (path: number[], filterData: SegmentFilterState<SegmentFilterCategorySpecificField>) => {
            setRootGroup((current) => updateFilterAtPath(current, path, filterData));

            if (filterData.category === FilterCategory.CONTACT_ACTIVITY && filterData.name) {
                setValuesRef(engagementNameToValuesRef[filterData.name]);
            }
        },
        [engagementNameToValuesRef]
    );

    const handleRemoveNode = useCallback((path: number[]) => {
        setRootGroup((current) => pruneEmptyGroups(removeNodeAtPath(current, path)));
    }, []);

    const handleUpdateMatchType = useCallback((path: number[], matchType: 'AND' | 'OR') => {
        setRootGroup((current) => updateMatchTypeAtPath(current, path, matchType));
    }, []);

    const handleAddFilter = useCallback((path: number[]) => {
        setRootGroup((current) => {
            const allFilters = flattenFilters(current);
            const lastFilter = allFilters[allFilters.length - 1];

            const newLeaf = createFilterLeafNode({
                id: getNextFilterId(),
                category: lastFilter?.category,
                categorySpecificFields: { value: '' },
                name: '',
                operator: undefined,
            });

            return addNodeAtPath(current, path, newLeaf);
        });
    }, []);

    const isRootInInitialState = (tree: FilterGroupNode): boolean => {
        if (tree.children.length !== 1) return false;
        const child = tree.children[0];
        if (child.type !== 'filter') return false;
        return !child.filter.name && !child.filter.operator;
    };

    const handleAddGroup = useCallback((path: number[]) => {
        setRootGroup((current) => {
            const defaultLeaf = createFilterLeafNode({
                id: getNextFilterId(),
                category: undefined,
                categorySpecificFields: { value: '' },
                name: '',
                operator: undefined,
            });

            const newGroup = createGroupNode('AND', [defaultLeaf]);

            if (path.length === 0 && isRootInInitialState(current)) {
                return { ...current, children: [newGroup] };
            }
            return addNodeAtPath(current, path, newGroup);
        });
    }, []);

    // ─── Record type change handling ────────────────────────────────────

    const getNewRecordTypes = (autocompleteOptions: StringOption[]): StringOption[] => {
        const isNewValueAllContacts = getHasAllContacts(autocompleteOptions);
        const isOldValueAllContacts = getHasAllContacts(recordTypeOptions);

        return isNewValueAllContacts
            ? isOldValueAllContacts
                ? removeAllContactsOption(autocompleteOptions)
                : removeOtherOptions(autocompleteOptions)
            : autocompleteOptions;
    };

    const changeRecordTypesAndFilters = (autocompleteOptions: StringOption[]) => {
        const newRecordTypes = getNewRecordTypes(autocompleteOptions);
        setRecordTypeOptions(newRecordTypes);
        setRecordTypeInputValue('');

        const newRecordTypeValues = getOptionValues(newRecordTypes);
        const newDemographicFilters = getDemographicFilters({
            hasAllContacts: getHasAllContacts(newRecordTypes),
            recordTypes: newRecordTypeValues,
            segmentRecordTypes,
        });
        const filterNameSet = new Set(newDemographicFilters.map((filter) => filter.name));

        setRootGroup((current) =>
            filterTree(
                current,
                (filter) => filter.category !== FilterCategory.CONTACT_DETAILS || filterNameSet.has(filter.name)
            )
        );
    };

    // ─── Prefill (edit/copy) ─────────────────────────────────────────────

    useEffect(() => {
        // Once filter metadata is ready, prime the engagement sends fetch if the initial definition
        // contains engagement filters. The ref guard ensures setValuesRef runs at most once.
        if (hasFetchedSendsRef.current || !initialDefinition) return;

        const firstEngagementName = findFirstEngagementName(initialDefinition);
        if (firstEngagementName && engagementNameToValuesRef[firstEngagementName]) {
            setValuesRef(engagementNameToValuesRef[firstEngagementName]);
            hasFetchedSendsRef.current = true;
        }
    }, [engagementNameToValuesRef, initialDefinition]);

    useEffect(() => {
        if (!isSegmentFiltersSuccess) return;

        if (!initialDefinition) {
            // Fresh builder: default to All Contacts + one empty filter.
            if (prefillRunRef.current) return;
            prefillRunRef.current = true;
            setRecordTypeOptions([allContactsRecordType]);
            handleAddFilter([]);
            return;
        }

        // Engagement filters need a send source loaded so the tree is built once with complete
        // data. `mergedSends` (not raw `sends`) is the correct gate here: it already folds in
        // `additionalSends` (the campaign's own in-flight sends), which for a conditional split's
        // own branch may be the ONLY source — a not-yet-persisted branch send has no DB record, so
        // the `useSends` API fetch behind `sends` can never resolve for it, and gating on `sends`
        // alone stalls the prefill (and this loading spinner) forever.
        const hasEngagement = Boolean(findFirstEngagementName(initialDefinition));
        if (hasEngagement && !mergedSends) return;
        if (prefillRunRef.current) return;
        prefillRunRef.current = true;

        const tree = buildTreeFromDefinition(initialDefinition, mergedSends, getNextFilterId);
        const recordTypeValues: StringOption[] = initialDefinition.contactRecordType?.map((value) => ({
            label: value,
            value,
        }));

        setRootGroup({ ...tree, id: 'root' });
        setRecordTypeOptions(recordTypeValues ?? [allContactsRecordType]);
        nextFilterIdRef.current = countFilters(tree);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSegmentFiltersSuccess, initialDefinition, sends, mergedSends]);

    // ─── Emit definition + validity on every change ──────────────────────

    const datetimeError = Object.values(datetimeErrors).includes(true);
    const isValid = isNestedSegmentFiltersValid(rootGroup, isRecordTypesEmpty) && !isRecordTypesEmpty && !datetimeError;

    useEffect(() => {
        const definition = getNestedSegmentDefinition({ rootGroup, hasAllContacts, recordTypes });
        onChange(definition, isValid);
        // onChange identity is owned by the consumer; depend only on inputs that affect the output.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootGroup, hasAllContacts, recordTypes, isValid]);

    const totalFilterCount = countFilters(rootGroup);
    const isMaxFilterLimit = totalFilterCount >= MAX_FILTER_LENGTH;
    const isAwaitingPrefill = Boolean(initialDefinition) && !prefillRunRef.current;

    if (!isSegmentFiltersSuccess || isAwaitingPrefill) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={28} role="progressbar" aria-label="Loading filters" />
            </Box>
        );
    }

    return (
        <Stack data-testid="nested-filter-builder">
            <Box mt={1} mb={3}>
                <AutocompleteInput
                    autocompleteValue={recordTypeOptions}
                    autocompleteInputValue={recordTypeInputValue}
                    multiple
                    onChange={changeRecordTypesAndFilters}
                    onInputChange={setRecordTypeInputValue}
                    options={segmentRecordTypeOptions}
                    placeholderName="Contact Type"
                    styles={autocompleteInputSX}
                    type="string"
                />
            </Box>
            <Stack mt={1} mb={1}>
                <Typography variant="h6">filters</Typography>
                <Typography variant="body2" marginTop=".5rem">
                    Add conditions to narrow down and define your audience.
                </Typography>
            </Stack>
            {recordTypeOptions.length > 0 && (
                <FilterGroup
                    group={rootGroup}
                    path={[]}
                    depth={0}
                    demographicFilters={demographicFilters}
                    engagementFilters={engagementFilters}
                    eventRecordTypeFilters={eventRecordTypeFilters}
                    objectRecordTypeFilters={objectRecordTypeFilters}
                    hasContactObjectFlag={!!hasContactObjectFlag}
                    sends={mergedSends}
                    handleDatetimeError={handleDatetimeError}
                    isMaxFilterLimit={isMaxFilterLimit}
                    onAddFilter={handleAddFilter}
                    onAddGroup={handleAddGroup}
                    onUpdateFilter={(path, filterData) => handleUpdateFilter(path, filterData)}
                    onRemoveNode={handleRemoveNode}
                    onUpdateMatchType={handleUpdateMatchType}
                />
            )}
        </Stack>
    );
};

export default NestedFilterBuilder;
